import React, { useEffect, useState, useCallback } from "react";
import {
  KeyRound, ShieldCheck, Users, ScrollText, Plus, Check, X, RotateCcw,
  Trash2, Lock, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PROVIDERS = ["cryptopanic", "newsapi", "coingecko", "coinglass", "defillama",
  "glassnode", "tradingeconomics", "fred", "sosovalue", "openai", "custom"];

const STATUS_COLORS = {
  PENDING: "var(--amber)", APPROVED: "var(--green)", REJECTED: "var(--red)", REVOKED: "var(--text-dim)",
};

function Panel({ children, className = "" }) {
  return (
    <div className={`rounded-2xl p-5 ${className}`} style={{ background: "var(--panel)", border: "1px solid var(--border)" }}>
      {children}
    </div>
  );
}

function Tag({ children, color }) {
  return (
    <span className="pill text-[10px] font-bold" style={{ color, borderColor: "var(--border)", background: "var(--panel-2)" }}>
      {children}
    </span>
  );
}

export default function AccessControl() {
  const { user, isOwner } = useAuth();
  const [tab, setTab] = useState("personal");

  const TABS = [
    { id: "personal", label: "Personal API Access", icon: KeyRound },
    ...(isOwner ? [
      { id: "workspace", label: "Workspace Integrations", icon: ShieldCheck },
      { id: "requests", label: "Access Requests", icon: ScrollText },
      { id: "team", label: "Team & Roles", icon: Users },
      { id: "audit", label: "Audit Log", icon: ScrollText },
    ] : []),
  ];

  return (
    <div className="space-y-5" data-testid="access-control-page">
      <div>
        <h1 className="text-xl font-bold">Access Control</h1>
        <p className="text-[13px]" style={{ color: "var(--text-muted)" }}>
          {isOwner ? "Manage workspace integrations, team roles and API access requests."
            : "Request and manage your personal API access. Owner approval required."}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className="flex items-center gap-2 rounded-lg px-3.5 h-9 text-[13px] font-medium transition-colors"
              style={{
                background: active ? "linear-gradient(90deg,rgba(139,92,246,.18),rgba(139,92,246,.04))" : "var(--panel-2)",
                border: active ? "1px solid rgba(139,92,246,.4)" : "1px solid var(--border)",
                color: active ? "#fff" : "var(--text-muted)",
              }}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "personal" && <PersonalTab />}
      {isOwner && tab === "workspace" && <WorkspaceTab />}
      {isOwner && tab === "requests" && <RequestsTab />}
      {isOwner && tab === "team" && <TeamTab me={user} />}
      {isOwner && tab === "audit" && <AuditTab />}
    </div>
  );
}

/* ---------------- Personal API access ---------------- */
function PersonalTab() {
  const [requests, setRequests] = useState([]);
  const [keys, setKeys] = useState([]);
  const [provider, setProvider] = useState("cryptopanic");
  const [purpose, setPurpose] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, k] = await Promise.all([api.getPersonalRequests(), api.getPersonalKeys()]);
      setRequests(Array.isArray(r) ? r : []);
      setKeys(Array.isArray(k) ? k : []);
    } catch (_) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (purpose.trim().length < 5) return toast.error("Purpose must be at least 5 characters");
    setBusy(true);
    try {
      await api.createPersonalRequest(provider, purpose.trim());
      setPurpose("");
      toast.success("Request submitted for owner approval");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally { setBusy(false); }
  };

  const approvedProviders = requests.filter((r) => r.status === "APPROVED").map((r) => r.provider);

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Panel>
        <div className="flex items-center gap-2 mb-4">
          <Plus size={16} style={{ color: "var(--accent-soft)" }} />
          <h3 className="font-semibold text-[15px]">Request Provider Access</h3>
        </div>
        <label className="text-[12px] font-medium" style={{ color: "var(--text-dim)" }}>Provider</label>
        <select data-testid="personal-provider-select" value={provider} onChange={(e) => setProvider(e.target.value)}
          className="mt-1.5 w-full rounded-lg h-10 px-3 text-[13px] outline-none"
          style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }}>
          {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <label className="text-[12px] font-medium mt-4 block" style={{ color: "var(--text-dim)" }}>Purpose</label>
        <textarea data-testid="personal-purpose-input" value={purpose} onChange={(e) => setPurpose(e.target.value)}
          placeholder="Why do you need this integration?" rows={3}
          className="mt-1.5 w-full rounded-lg p-3 text-[13px] outline-none resize-none"
          style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }} />
        <button data-testid="submit-personal-request" disabled={busy} onClick={submit}
          className="mt-4 w-full h-10 rounded-lg font-semibold text-[13px] disabled:opacity-50"
          style={{ background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", color: "#fff" }}>
          {busy ? "Submitting…" : "Submit Request"}
        </button>

        {approvedProviders.length > 0 && (
          <div className="mt-6 pt-5" style={{ borderTop: "1px solid var(--border)" }}>
            <h4 className="text-[13px] font-semibold mb-2">Add your key (approved providers)</h4>
            <ApprovedKeyForm providers={approvedProviders} onSaved={load} />
          </div>
        )}
      </Panel>

      <Panel>
        <h3 className="font-semibold text-[15px] mb-4">My Requests</h3>
        <div className="space-y-2.5" data-testid="personal-requests-list">
          {requests.length === 0 && <div className="text-[13px]" style={{ color: "var(--text-dim)" }}>No requests yet.</div>}
          {requests.map((r) => (
            <div key={r.id} className="rounded-lg p-3 flex items-start justify-between gap-3"
              style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[13px]">{r.provider}</span>
                  {keys.find((k) => k.provider === r.provider) && <Tag color="var(--green)">KEY SET</Tag>}
                </div>
                <div className="text-[12px] truncate" style={{ color: "var(--text-dim)" }}>{r.purpose}</div>
              </div>
              <Tag color={STATUS_COLORS[r.status]}>{r.status}</Tag>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function ApprovedKeyForm({ providers, onSaved }) {
  const [provider, setProvider] = useState(providers[0]);
  const [secret, setSecret] = useState("");
  const save = async () => {
    if (!secret) return toast.error("Enter your API key");
    try {
      await api.addPersonalKey(provider, secret);
      setSecret("");
      toast.success("Key saved securely (encrypted)");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save key");
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <select data-testid="personal-key-provider" value={provider} onChange={(e) => setProvider(e.target.value)}
        className="rounded-lg h-9 px-3 text-[13px] outline-none"
        style={{ background: "var(--panel)", border: "1px solid var(--border)", color: "var(--text)" }}>
        {providers.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <div className="flex gap-2">
        <input data-testid="personal-key-input" type="password" value={secret} onChange={(e) => setSecret(e.target.value)}
          placeholder="Paste API key" className="flex-1 rounded-lg h-9 px-3 text-[13px] outline-none"
          style={{ background: "var(--panel)", border: "1px solid var(--border)", color: "var(--text)" }} />
        <button data-testid="save-personal-key" onClick={save} className="px-4 h-9 rounded-lg text-[13px] font-semibold"
          style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }}>Save</button>
      </div>
    </div>
  );
}

/* ---------------- Owner: workspace keys ---------------- */
function WorkspaceTab() {
  const [data, setData] = useState({ encryptionEnabled: true, keys: [] });
  const [provider, setProvider] = useState("cryptopanic");
  const [secret, setSecret] = useState("");
  const load = useCallback(async () => {
    try { setData(await api.getWorkspaceKeys()); } catch (_) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!secret) return toast.error("Enter a secret");
    try {
      await api.setWorkspaceKey(provider, secret);
      setSecret("");
      toast.success("Workspace key saved (encrypted)");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (p) => {
    try { await api.delWorkspaceKey(p); toast.success("Removed"); load(); }
    catch (_) { toast.error("Delete failed"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Panel>
        <div className="flex items-center gap-2 mb-4">
          <Lock size={16} style={{ color: "var(--accent-soft)" }} />
          <h3 className="font-semibold text-[15px]">Add Workspace Integration</h3>
        </div>
        {!data.encryptionEnabled && (
          <div className="mb-4 rounded-lg p-3 flex items-center gap-2 text-[12px]"
            style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", color: "var(--red)" }}>
            <AlertTriangle size={15} /> Encryption not configured — key saving is disabled.
          </div>
        )}
        <select data-testid="ws-provider-select" value={provider} onChange={(e) => setProvider(e.target.value)}
          className="w-full rounded-lg h-10 px-3 text-[13px] outline-none"
          style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }}>
          {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <input data-testid="ws-secret-input" type="password" value={secret} onChange={(e) => setSecret(e.target.value)}
          placeholder="API secret" className="mt-3 w-full rounded-lg h-10 px-3 text-[13px] outline-none"
          style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }} />
        <button data-testid="save-ws-key" disabled={!data.encryptionEnabled} onClick={save}
          className="mt-4 w-full h-10 rounded-lg font-semibold text-[13px] disabled:opacity-40"
          style={{ background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", color: "#fff" }}>Save Integration</button>
      </Panel>

      <Panel>
        <h3 className="font-semibold text-[15px] mb-4">Configured Integrations</h3>
        <div className="space-y-2.5" data-testid="ws-keys-list">
          {data.keys.length === 0 && <div className="text-[13px]" style={{ color: "var(--text-dim)" }}>No integrations configured.</div>}
          {data.keys.map((k) => (
            <div key={k.provider} className="rounded-lg p-3 flex items-center justify-between"
              style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
              <div>
                <div className="font-semibold text-[13px]">{k.provider}</div>
                <div className="text-[12px] tabnum" style={{ color: "var(--text-dim)" }}>{k.masked}</div>
              </div>
              <button data-testid={`del-ws-${k.provider}`} onClick={() => remove(k.provider)}
                className="grid place-items-center rounded-lg" style={{ width: 32, height: 32, background: "var(--panel)", border: "1px solid var(--border)" }}>
                <Trash2 size={15} style={{ color: "var(--red)" }} />
              </button>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

/* ---------------- Owner: access requests ---------------- */
function RequestsTab() {
  const [requests, setRequests] = useState([]);
  const load = useCallback(async () => {
    try { setRequests(await api.getPersonalRequests()); } catch (_) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (rid, decision) => {
    try { await api.decideRequest(rid, decision); toast.success(`Request ${decision.toLowerCase()}`); load(); }
    catch (_) { toast.error("Action failed"); }
  };

  return (
    <Panel>
      <h3 className="font-semibold text-[15px] mb-4">All Access Requests</h3>
      <div className="space-y-2.5" data-testid="owner-requests-list">
        {requests.length === 0 && <div className="text-[13px]" style={{ color: "var(--text-dim)" }}>No requests.</div>}
        {requests.map((r) => (
          <div key={r.id} className="rounded-lg p-3.5 flex flex-wrap items-center justify-between gap-3"
            style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[13px]">{r.provider}</span>
                <Tag color={STATUS_COLORS[r.status]}>{r.status}</Tag>
              </div>
              <div className="text-[12px]" style={{ color: "var(--text-dim)" }}>{r.email} — {r.purpose}</div>
            </div>
            <div className="flex gap-2">
              {r.status === "PENDING" && <>
                <button data-testid={`approve-${r.id}`} onClick={() => decide(r.id, "APPROVED")}
                  className="flex items-center gap-1.5 px-3 h-8 rounded-lg text-[12px] font-semibold"
                  style={{ background: "rgba(34,197,94,.12)", color: "var(--green)", border: "1px solid rgba(34,197,94,.3)" }}>
                  <Check size={14} /> Approve</button>
                <button data-testid={`reject-${r.id}`} onClick={() => decide(r.id, "REJECTED")}
                  className="flex items-center gap-1.5 px-3 h-8 rounded-lg text-[12px] font-semibold"
                  style={{ background: "rgba(239,68,68,.12)", color: "var(--red)", border: "1px solid rgba(239,68,68,.3)" }}>
                  <X size={14} /> Reject</button>
              </>}
              {r.status === "APPROVED" && (
                <button data-testid={`revoke-${r.id}`} onClick={() => decide(r.id, "REVOKED")}
                  className="flex items-center gap-1.5 px-3 h-8 rounded-lg text-[12px] font-semibold"
                  style={{ background: "var(--panel)", color: "var(--text-muted)", border: "1px solid var(--border)" }}>
                  <RotateCcw size={14} /> Revoke</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* ---------------- Owner: team & roles ---------------- */
function TeamTab({ me }) {
  const [users, setUsers] = useState([]);
  const load = useCallback(async () => {
    try { setUsers(await api.listUsers()); } catch (_) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } };

  return (
    <Panel>
      <h3 className="font-semibold text-[15px] mb-4">Team & Roles</h3>
      <div className="space-y-2.5" data-testid="team-list">
        {users.map((u) => {
          const owner = u.role === "OWNER";
          return (
            <div key={u.user_id} className="rounded-lg p-3.5 flex flex-wrap items-center justify-between gap-3"
              style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="grid place-items-center rounded-lg text-[12px] font-bold text-white shrink-0"
                  style={{ width: 34, height: 34, background: "linear-gradient(135deg,#8b5cf6,#6d28d9)" }}>
                  {(u.name || u.email || "?")[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[13px] truncate">{u.email}</span>
                    <Tag color={owner ? "var(--accent-soft)" : u.role === "ADMIN" ? "var(--amber)" : "var(--text-muted)"}>{u.role}</Tag>
                    {u.status === "suspended" && <Tag color="var(--red)">SUSPENDED</Tag>}
                  </div>
                  <div className="text-[11px]" style={{ color: "var(--text-dim)" }}>
                    {u.terms_accepted ? "Terms accepted" : "Terms pending"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {owner ? (
                  <Tag color="var(--accent-soft)">OWNER (protected)</Tag>
                ) : (
                  <>
                    <select data-testid={`role-select-${u.user_id}`} value={u.role}
                      onChange={(e) => act(() => api.setRole(u.user_id, e.target.value), "Role updated")}
                      className="rounded-lg h-8 px-2 text-[12px] outline-none"
                      style={{ background: "var(--panel)", border: "1px solid var(--border)", color: "var(--text)" }}>
                      <option value="USER">USER</option>
                      <option value="ADMIN">ADMIN</option>
                    </select>
                    {u.status === "suspended" ? (
                      <button data-testid={`restore-${u.user_id}`} onClick={() => act(() => api.restoreUser(u.user_id), "User restored")}
                        className="px-3 h-8 rounded-lg text-[12px] font-semibold"
                        style={{ background: "rgba(34,197,94,.12)", color: "var(--green)", border: "1px solid rgba(34,197,94,.3)" }}>Restore</button>
                    ) : (
                      <button data-testid={`suspend-${u.user_id}`} onClick={() => act(() => api.suspendUser(u.user_id), "User suspended")}
                        className="px-3 h-8 rounded-lg text-[12px] font-semibold"
                        style={{ background: "rgba(239,68,68,.12)", color: "var(--red)", border: "1px solid rgba(239,68,68,.3)" }}>Suspend</button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

/* ---------------- Owner: audit log ---------------- */
function AuditTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.getAudit().then(setLogs).catch(() => {}); }, []);
  return (
    <Panel>
      <h3 className="font-semibold text-[15px] mb-4">Audit Log</h3>
      <div className="space-y-1.5 max-h-[540px] overflow-y-auto" data-testid="audit-list">
        {logs.length === 0 && <div className="text-[13px]" style={{ color: "var(--text-dim)" }}>No activity yet.</div>}
        {logs.map((l) => (
          <div key={l.id} className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-[12px]"
            style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold" style={{ color: "var(--accent-soft)" }}>{l.action}</span>
              <span className="truncate" style={{ color: "var(--text-dim)" }}>{l.email || "—"}</span>
            </div>
            <span className="tabnum shrink-0" style={{ color: "var(--text-dim)" }}>{new Date(l.at).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}
